import { useState } from "react";

import type { CycleDay, CycleRun } from "../domain/cyclePatterns";
import { DIARY_WORDS, dayTitle } from "../domain/cycleWords";
import { addDays, todayStr } from "../domain/date";
import { dayWords } from "../domain/symptoms";
import { ActionBar } from "./ActionBar";
import { Field } from "./Fields";
import { I } from "./icons";
import { NavRow } from "./Rows";
import { Sheet } from "./Sheet";

/** How far back a day can be opened: the diary's six cycles, give or take. */
export const DIARY_DAYS_BACK = 180;

/**
 * One logged day in the symptom diary (ADR-060): the date and cycle day, then what was logged in
 * `dayWords`' order. A period day carries the cycle tile, any other day a plain calendar one,
 * so the rows line up. Tapping opens the day's sheet.
 */
export function DiaryDayRow({ c, d, onOpen }: { c: CycleRun; d: CycleDay; onOpen: (ds: string) => void }) {
  const period = d.day <= c.periodDays;
  return (
    <NavRow
      title={dayTitle(d.ds, d.day, period)}
      subtitle={dayWords(d.keys).join(", ") || undefined}
      icon={period ? I.drop : I.calendar}
      tone={period ? "cycle" : "default"}
      onClick={() => onOpen(d.ds)}
    />
  );
}

/**
 * "Another day" on the symptom diary (ADR-060): one date field, then Open hands the day to the
 * caller, which opens the CycleSheet for it. A day in the future or more than six months back is
 * refused with the sheet's own line rather than opened empty.
 */
export function DiaryDaySheet({ onClose, onOpen }: { onClose: () => void; onOpen: (ds: string) => void }) {
  const today = todayStr();
  const min = addDays(today, -DIARY_DAYS_BACK);
  const [ds, setDs] = useState(addDays(today, -1));
  const tooOld = !!ds && ds < min;
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(ds) && !tooOld && ds <= today;
  return (
    <Sheet
      title={DIARY_WORDS.which}
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: DIARY_WORDS.open, disabled: !valid, onClick: () => valid && onOpen(ds) }} />}
    >
      <Field
        label={DIARY_WORDS.date}
        type="date"
        value={ds}
        min={min}
        max={today}
        onChange={setDs}
        invalid={tooOld}
        hint={tooOld ? DIARY_WORDS.tooOld : undefined}
      />
    </Sheet>
  );
}
